/*
 * qp-cart-fit-notice.js — <qp-fit-ack> custom element.
 * Cart-level "fit details missing" acknowledgment (mirrors the checkout
 * app extension's banner one step earlier). Display-only except the
 * checkbox, which posts the acknowledgment onto cart.attributes so
 * checkout opens satisfied without a full page reload.
 *
 * DOES NOT touch line-item properties, sa-cart-sync.js DOM hooks, or any
 * `.m-cart-item` / data-key / data-index / updates[] contract.
 *
 * Multiple instances can be mounted at once (cart page renders it twice —
 * desktop sidebar + mobile sticky footer — and the drawer renders its
 * own), all sharing one cart's ack state. This module keeps every
 * instance's checkbox/error in sync and sequences requests so a stale
 * response can never win a race against a newer one.
 */
(function () {
  if (typeof window === 'undefined' || !window.customElements) return;
  if (customElements.get('qp-fit-ack')) return;

  var registry = [];
  var ackSeq = 0;
  var staleAckCleared = false;
  var sectionRefreshWired = false;
  var sectionSeq = 0;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function isCartPage() {
    if (window.MinimogSettings && window.MinimogSettings.templateName) {
      return window.MinimogSettings.templateName === 'cart';
    }
    return !!(document.body && document.body.classList.contains('template-cart'));
  }

  // The drawer can be open (and mounted) on ANY page, but the section
  // refresh below only ever fetches the cart-template (page) section —
  // never wire it up from a drawer-only context, and always exclude
  // drawer instances from the page-side reconciliation so page markup
  // (missing form="cart-drawer-form" / --drawer styling) never clobbers
  // the drawer's own qp-fit-ack.
  function pageOnlyFitAcks(root) {
    return Array.prototype.filter.call(root.querySelectorAll('qp-fit-ack'), function (el) {
      return !el.closest('m-cart-drawer');
    });
  }

  function setAllChecked(checked) {
    registry.forEach(function (el) {
      var cb = el.querySelector('input[type="checkbox"]');
      if (cb) cb.checked = checked;
    });
  }

  function hideAllErrors() {
    registry.forEach(function (el) {
      var err = el.querySelector('.qp-fit-notice__error');
      if (err) err.hidden = true;
    });
  }

  function showAllErrors() {
    registry.forEach(function (el) {
      var err = el.querySelector('.qp-fit-notice__error');
      if (err) err.hidden = false;
    });
  }

  // F6: every call gets its own sequence number; only the settlement of the
  // MOST RECENT call is allowed to act. A superseded response — success or
  // failure — is a silent no-op.
  function postAck(value, onSettled) {
    var seq = ++ackSeq;
    fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _fit_details_ack: value } }),
    })
      .then(function (response) {
        if (seq !== ackSeq) return;
        if (response.ok) {
          hideAllErrors();
          if (onSettled) onSettled(true);
        } else if (onSettled) {
          onSettled(false);
        }
      })
      .catch(function () {
        if (seq !== ackSeq) return;
        if (onSettled) onSettled(false);
      });
  }

  // F3 (page path only): the drawer's fit-notice lives inside
  // [data-cart-subtotal] so drawer AJAX naturally refreshes it. The cart
  // PAGE has no such container for this notice, so on every cart update we
  // pull a fresh cart-template section over the Section Rendering API and
  // reconcile — but ONLY on the cart page itself. On any other page the
  // only qp-fit-ack in the document is the drawer's, and cart-template
  // markup must never be used to replace it (wrong form= wiring, wrong
  // --drawer styling) — also avoids a needless fetch on every drawer open.
  function wireSectionRefresh() {
    if (sectionRefreshWired) return;
    if (!isCartPage()) return;
    if (typeof window.MinimogEvents === 'undefined' || typeof window.MinimogTheme === 'undefined') return;
    var pubSubEvents = window.MinimogTheme.pubSubEvents;
    if (!pubSubEvents || !pubSubEvents.cartUpdate || typeof window.MinimogEvents.subscribe !== 'function') return;
    sectionRefreshWired = true;

    window.MinimogEvents.subscribe(pubSubEvents.cartUpdate, function () {
      var seq = ++sectionSeq;
      fetch(rootUrl() + 'cart?sections=cart-template')
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (seq !== sectionSeq) return; // superseded — ignore
          var html = data && data['cart-template'];
          if (!html) return;
          var freshDoc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = pageOnlyFitAcks(freshDoc);
          var current = pageOnlyFitAcks(document);
          var max = Math.max(fresh.length, current.length);
          for (var i = 0; i < max; i++) {
            if (fresh[i] && current[i]) {
              current[i].outerHTML = fresh[i].outerHTML;
            } else if (!fresh[i] && current[i]) {
              current[i].remove();
            }
            // fresh[i] with no current[i] (a line newly became offending
            // without the notice already present in the DOM) is a rare
            // edge case left alone here — the notice appears on next
            // full section render (e.g. add/remove line).
          }
        })
        .catch(function () {});
    });
  }

  class QpFitAck extends HTMLElement {
    connectedCallback() {
      registry.push(this);
      this._checkbox = this.querySelector('input[type="checkbox"]');
      this._error = this.querySelector('.qp-fit-notice__error');
      if (this._checkbox) {
        this._onChange = this._onChange.bind(this);
        this._checkbox.addEventListener('change', this._onChange);
      }
      this._maybeClearStaleAck();
      wireSectionRefresh();
    }

    disconnectedCallback() {
      var idx = registry.indexOf(this);
      if (idx > -1) registry.splice(idx, 1);
      if (this._checkbox) this._checkbox.removeEventListener('change', this._onChange);
    }

    // F4: the box only pre-checks on an exact stored-value match (see
    // qp-cart-fit-notice.liquid); if the store carries a DIFFERENT
    // non-blank ack (stale — cart changed since the buyer last ticked it),
    // clear it once so checkout's own re-sync starts clean.
    _maybeClearStaleAck() {
      if (staleAckCleared) return;
      var stored = this.dataset.ackStored || '';
      var current = this.dataset.ackValue || '';
      if (stored !== '' && stored !== current) {
        staleAckCleared = true;
        postAck('', function () {});
      }
    }

    _onChange(event) {
      var checkbox = event.currentTarget;
      var checked = checkbox.checked;
      var value = checked ? this.dataset.ackValue : '';
      setAllChecked(checked);
      postAck(value, function (ok) {
        if (!ok) {
          setAllChecked(!checked);
          showAllErrors();
        }
      });
    }
  }

  customElements.define('qp-fit-ack', QpFitAck);
})();
